/* eslint-disable prefer-promise-reject-errors */
import axios, { AxiosRequestConfig, AxiosResponse, AxiosInstance } from 'axios';
import qs from 'qs';
import {
  RESPONSE_SUCCESS_CODE,
  REPONSE_ERROR_MSG,
  RESPONSE_LOGIN_TIMEOUT
} from '@configs/constants';
import { ApiConfig, Response } from '@schemas/service';
import Toast from '@components/Toast';
import { debugConsole } from '@aio/utils';
import aioWeblog from '@aio/weblog';
import { ERROR_MESSAGE_SHOW_WHITE_LIST } from '@/configs/errors';
import weakNetwork from './weak-network';
import globalLoading from './global-loading';
import SameRequestUtils from './same-request-utils';
import { headerRequestInterceptor, sig4RequestInterceptor } from './apiSig';

interface CustomAxiosInstance<R = any, V = any> extends AxiosInstance {
  /** 自定义 axios response 拦截器 */
  responseProcessor?(response: AxiosResponse<R>): V | Promise<V>;
}

const FMPApi = [
  {
    pathname: '/',
    api: [
      '/health/api/v1/home/queryMainFeatureEntrance',
      '/health/api/v1/home/queryFeatureEntrance',
      '/health/api/v1/contentRecommend/homeRecommendDoctorTab',
      '/health/api/v1/contentRecommend/homeRecommendDoctor',
      '/health/api/v1/liveColumn/doctorFaceToFace',
      '/health/api/v1/home/queryHotHealthVideo'
    ]
  },
  {
    pathname: '/med-dictionary',
    api: '/health/api/v2/medical-dict/departments'
  }
];

const FMPPathname = FMPApi.map((val) => val.pathname);
const FMPCache = {};

export function triggerFMP(url: string, pathname?: string) {
  const currentPage = pathname || window.location.pathname;
  if (FMPPathname.includes(currentPage) && !FMPCache[currentPage]) {
    if (
      FMPApi.find((val) => {
        if (val.pathname !== pathname) {
          return false;
        }
        if (Array.isArray(val.api)) {
          return val.api.find((item) => item.includes(url));
        }
        return val.api.includes(url);
      })
    ) {
      FMPCache[currentPage] = true;
      aioWeblog.fmp();
      debugConsole(`手动触发 FMP 统计: ${currentPage}`);
    }
  }
}

export const axiosInstance: CustomAxiosInstance = axios.create({
  // baseURL: process.env.NX_APP_API_CONTEXT,
  // withCredentials: true,
  // eslint-disable-next-line
  timeout: Number(process.env.REQUEST_TIMEOUT!) || 10 * 1000,
  paramsSerializer(params) {
    const paramStr = qs.stringify(params, {
      arrayFormat: 'brackets'
    });
    return paramStr;
  }
});
axiosInstance.responseProcessor = processResponse;
axiosInstance.interceptors.request.use(sig4RequestInterceptor); // 添加 Sig4 请求拦截器
axiosInstance.interceptors.request.use(headerRequestInterceptor);

export function sendSentryError(error, type) {
  // Sentry 已移除，此方法为空实现
  console.log('[Sentry Mock] sendSentryError:', type, error);
}

export function sendSentryMessage(axioResponse: AxiosResponse<any>) {
  // Sentry 已移除，此方法为空实现
  const { config, data, status } = axioResponse;
  console.log('[Sentry Mock] sendSentryMessage:', {
    response: data,
    request: { url: config.url, params: config.params, data: config.data },
    httpStatus: status
  });
}

// 发送请求前，对数据进行处理
function processRequest(config: AxiosRequestConfig) {
  const result = config;
  result.method = config.method || 'GET';

  return result;
}

// 请求返回后，对返回进行处理
function processResponse(response: AxiosResponse<Response>) {
  const {
    config: { url, hideToast = false, needResponse = false },
    data: { code, success, message: errorMsg = REPONSE_ERROR_MSG }
    // status
  } = response as any;

  // 适配请求需要全部 response，这里有点奇怪，在下游的 then 分支里处理错误码...
  // TODO: 待优化，建议不要使用该配置了
  if (needResponse) {
    if (code && +code !== RESPONSE_SUCCESS_CODE) {
      sendSentryMessage(response);
    }
    return Promise.resolve(response.data);
  }
  // 统一判断错误码，下游走 catch 分支
  if (code && +code !== RESPONSE_SUCCESS_CODE) {
    sendSentryMessage(response);
    return Promise.reject({
      ...response.data,
      code,
      success,
      message: errorMsg
    });
  }
  // 兼容code不存在，用success判断，下游走 catch 分支
  if (code === undefined && !success) {
    // 登录过期
    // response.data 返回：{"loginUrl":"https://ksid.test.gifshow.com/pass/kuaishou/login/passToken?sid=kuaishou.api&callback=https%3A%2F%2Fks-health.test.gifshow.com%2Frest%2Finfra%2Fsts%3FfollowUrl%3Dhttps%253A%252F%252Fks-health.test.gifshow.com%252F","result":109,"error_id":"https://infra-admin.corp.kuaishou.com/rest/admin/passport/tokenerrid?errorId="}
    if (response.data.result === RESPONSE_LOGIN_TIMEOUT) {
      const errMsg = '已过期，请您重新登录';
      return Promise.reject({
        code: RESPONSE_LOGIN_TIMEOUT,
        message: errMsg
      });
    }
    sendSentryMessage(response);
    return Promise.reject({
      ...response.data,
      code,
      success,
      message: errorMsg
    });
  }

  const { pathname } = window.location;
  triggerFMP(url, pathname);
  // 错误码为0，下游走 then 分支
  return Promise.resolve(response.data);
}

// 请求返回错误后，对错误进行处理
const processResponseError = (error) => {
  if (!axios.isCancel(error)) {
    sendSentryError(error, 'response interceptors');
  }
  return Promise.reject(error);
};

// 对请求进行拦截
axiosInstance.interceptors.request.use(
  (config) => {
    SameRequestUtils.removePendingRequest(config); // 检查是否存在重复请求，若存在则取消已发的请求
    SameRequestUtils.addPendingRequest(config); // 把当前请求信息添加到pendingRequest对象中
    return processRequest(config);
  },
  (error) => {
    sendSentryError(error, 'request interceptors');
    Promise.reject(error);
  }
);

// 对返回进行拦截
axiosInstance.interceptors.response.use(
  (response) => {
    SameRequestUtils.removePendingRequest(response.config); // 从pendingRequest对象中移除请求
    const processor = axiosInstance.responseProcessor;
    if (processor) {
      return processor(response);
    }
    return response;
  },
  (error) => {
    SameRequestUtils.removePendingRequest(error.config || {}); // 从pendingRequest对象中移除请求
    if (axios.isCancel(error)) {
      debugConsole(`已取消的重复请求：${error.message}`);
    }
    return processResponseError(error);
  }
);

interface IOptions {
  hideToast?: boolean;
  needResponse?: boolean;
  hideWeakNetworkToast?: boolean;
  hideGlobalLoading?: boolean;
}

export type TAxiosRequestConfig = AxiosRequestConfig & IOptions;

/**
 * 发送请求
 * /api/v1/foo/{id}/bar => request(apiConfig, {id: 1})
 * /api/v1/foo/bar?id=1 => request(apiConfig, {id: 1})
 * @param api 对象 {url, method}
 * @param params 组成 url 的变量，包含查询字符串和路径参数（如果有）
 * @param data 需要发送的数据，body payload(post, put, ...)
 * @param options 除url，method外的axios配置选项(AxiosRequestConfig)
 */
async function request<R = any>(
  api: ApiConfig,
  params?: Record<string, unknown>,
  data?: any,
  options?: Omit<AxiosRequestConfig, 'url' | 'method'> & IOptions
) {
  let config: TAxiosRequestConfig = {
    hideGlobalLoading: true,
    ...api,
    ...options
  };

  // 解析 param
  if (params) {
    const url = api.url?.replace(/\{([^}]+)\}/g, (all, param) => {
      if (param in params) {
        const match = params[param];
        // eslint-disable-next-line no-param-reassign
        delete params[param];
        return match;
      }
      return param;
    });
    config = { ...config, url, params };
  }

  // 附带 data 数据
  if (data) {
    config = {
      ...config,
      data
    };
  }

  // 核酸检测相关接口超时时间
  if (api.url!.includes('nucleicAcid')) {
    config.timeout = 150 * 1000;
    config.hideWeakNetworkToast = true;
  }

  // 挂号需求调用山大二院相关接口请求时间比较长
  if (api.url?.toLowerCase().includes('regist')) {
    config.timeout = 60 * 1000;
    config.hideWeakNetworkToast = true;
  }

  // 弱网，请求超时 2s 后的提示语
  if (!config.hideWeakNetworkToast) {
    weakNetwork.addRequestCount(config.url);
    weakNetwork.mayDoTimeout(() => {
      // Toast.show('请检查网络是否正常');
      // Sentry 已移除，此调用已注释
      console.log('[Sentry Mock] 弱网提示:', `${api.url}, 弱网提示`);
    }, config.url);
  }

  // 全局加载中状态
  if (!config.hideGlobalLoading) {
    globalLoading.addRequestCount(config.url);
  }

  return axiosInstance
    .request<any, R>(config)
    .catch((e) => {
      if (!axios.isCancel(e) && (options == null || !options.hideToast)) {
        if (e.message.includes('timeout')) {
          // Toast.show(`当前网络速度慢，请耐心等候`);
        } else if (
          e.code &&
          ERROR_MESSAGE_SHOW_WHITE_LIST[e.code] !== undefined
        ) {
          // 白名单内的错误码需要透出给用户
          Toast.show(e.message || REPONSE_ERROR_MSG);
        } else {
          // 其余错误透出网络异常
          Toast.show('网络异常');
        }
      }

      if (e.message.includes('timeout')) {
        return Promise.reject(new Error('当前网络速度慢，请耐心等候'));
      }

      return Promise.reject(e);
    })
    .finally(() => {
      // 弱网，请求超时 2s 后的提示语
      if (!config.hideWeakNetworkToast) {
        weakNetwork.removeRequestCount(config.url);
      }

      // 全局加载中状态
      if (!config.hideGlobalLoading) {
        globalLoading.removeRequestCount(config.url);
      }
    });
}

export { request };
